import { useCallback, useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

import { useOverviewVariables } from './useOverviewVariables';
import type { MetricKey } from '../components/AnalyticsOverview/overviewChartConfig';

export interface TimeseriesRow {
  time_bucket: string;
  avg_csat_score: number | null;
  /** The two components avg_csat_score pools, for the hovered day's review split. */
  avg_ai_csat_insights: number | null;
  ai_csat_insights_count: number | null;
  avg_csat_user_rating: number | null;
  csat_user_rating_count: number | null;
  containment_rate: number | null;
  total_conversations: number | null;
}

interface QueryResponse {
  rows: TimeseriesRow[];
}

export interface CsatBreakdown {
  avgUserRating: number | null;
  userRatingCount: number | null;
  avgAiInsights: number | null;
  aiInsightsCount: number | null;
}

export interface TimeseriesDataPoint {
  date: string;
  value: number;
  /** Containment rate for the day – attached when viewing totalConversations to compute AI/Human split */
  containmentRate?: number | null;
  /** Review breakdown for the day – attached when viewing csat so the tooltip tracks the hovered date */
  csatBreakdown?: CsatBreakdown;
  /** Number of reviews behind the day's value – shown in the tooltip when present */
  reviewCount?: number;
}

export const ACCOUNT_CONVERSATIONS_TIMESERIES = gql`
  query AccountConversationsTimeseries(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $channels: _text
    $deskIds: _uuid
    $isTest: Boolean
    $minNumUserMessages: Int
    $minAvgCsatScore: Float
    $maxAvgCsatScore: Float
  ) {
    rows: account_conversations_timeseries(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        channels: $channels
        desk_ids: $deskIds
        is_test: $isTest
        min_num_user_messages: $minNumUserMessages
        min_avg_csat_score: $minAvgCsatScore
        max_avg_csat_score: $maxAvgCsatScore
      }
    ) {
      time_bucket
      avg_csat_score
      avg_ai_csat_insights
      ai_csat_insights_count
      avg_csat_user_rating
      csat_user_rating_count
      containment_rate
      total_conversations
    }
  }
`;

export const METRIC_FIELD_MAP: Record<MetricKey, keyof TimeseriesRow> = {
  csat: 'avg_csat_score',
  containment: 'containment_rate',
  totalConversations: 'total_conversations',
};

/** Round to the tooltip's display precision so the rows can't disagree with the point. */
const round1 = (value: number): number => Number(value.toFixed(1));

/**
 * One side of the day's review split. A score is shown only when the day has at
 * least one review of that kind AND an average to show; otherwise it stays null
 * and the tooltip renders "–". Never coerce a missing score to 0 — that invents
 * a review nobody left. Today the backend returns a null average over zero
 * reviews, so either guard alone would do; requiring both keeps the tooltip
 * honest without depending on that.
 */
const dailyScore = (avg: number | null, count: number | null): number | null =>
  (count ?? 0) > 0 && avg != null ? round1(avg) : null;

/** The hovered day's review split, as the tooltip renders it. */
const toDailyCsatBreakdown = (row: TimeseriesRow): CsatBreakdown => ({
  avgUserRating: dailyScore(row.avg_csat_user_rating, row.csat_user_rating_count),
  userRatingCount: row.csat_user_rating_count ?? 0,
  avgAiInsights: dailyScore(row.avg_ai_csat_insights, row.ai_csat_insights_count),
  aiInsightsCount: row.ai_csat_insights_count ?? 0,
});

/**
 * Transforms raw timeseries rows into chart-ready data points for the selected metric.
 * Filters out null values and converts containment from a 0–1 fraction to a percentage.
 */
export const transformTimeseriesToChartData = (
  rows: TimeseriesRow[] | undefined,
  selectedMetric: MetricKey
): TimeseriesDataPoint[] => {
  if (!rows?.length) {
    return [];
  }

  const field = METRIC_FIELD_MAP[selectedMetric];

  return rows.reduce<TimeseriesDataPoint[]>((acc, row) => {
    const raw = row[field];
    if (raw == null) {
      return acc;
    }

    const value =
      selectedMetric === 'containment'
        ? Math.round((raw as number) * 100) // 0–1 fraction → percentage
        : (raw as number);

    const point: TimeseriesDataPoint = { date: row.time_bucket, value };

    if (selectedMetric === 'totalConversations') {
      point.containmentRate = row.containment_rate;
    }

    if (selectedMetric === 'csat') {
      point.csatBreakdown = toDailyCsatBreakdown(row);
    }

    acc.push(point);
    return acc;
  }, []);
};

export const useOverviewTimeseries = (selectedMetric: MetricKey, skipQuery = false) => {
  const { variables, skip } = useOverviewVariables();

  const { loading, data, error, refetch } = useQuery<QueryResponse>(
    ACCOUNT_CONVERSATIONS_TIMESERIES,
    { variables, skip: skip || skipQuery }
  );

  const chartData = useMemo(
    () => transformTimeseriesToChartData(data?.rows, selectedMetric),
    [data?.rows, selectedMetric]
  );

  const onRefetch = useCallback(() => {
    refetch(variables);
  }, [refetch, variables]);

  return { isLoading: loading, error, data: chartData, onRefetch };
};
